import { getPdfRenderTimestamp } from './pdfRenderTime';

export type PaymentCalendarInput = {
  createdAt?: string;
  tripStartDate?: string;
  flightCost: number;
  hotelCost: number;
  cabCost: number;
  totalPackageCost: number;
};

export type PaymentCalendarMilestone = {
  step: number;
  title: 'Booking Amount' | 'Balance Payment' | 'Full Payment';
  amount: number;
  dueAt: string;
  dueFormat: 'date' | 'dateTime';
  note: string;
};

export type PaymentCalendarResult =
  | {
    kind: 'ok';
    milestones: PaymentCalendarMilestone[];
    daysUntilTrip: number;
  }
  | {
    kind: 'error';
    message: string;
  };

const DAY_MS = 24 * 60 * 60 * 1000;

const isFiniteAmount = (value: number) => Number.isFinite(value) && value >= 0;

const startOfLocalDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const parseLocalDate = (value: string) => {
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  return new Date(value);
};

const toLocalDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addHours = (date: Date, hours: number) => new Date(date.getTime() + hours * 60 * 60 * 1000);

const addCalendarDays = (date: Date, days: number) => {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

export const formatPaymentDateTime = (value: string) => {
  const date = parseLocalDate(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const formatPaymentDate = (value: string) => {
  const date = parseLocalDate(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const buildPaymentCalendar = ({
  createdAt,
  tripStartDate,
  flightCost,
  hotelCost,
  cabCost,
  totalPackageCost,
}: PaymentCalendarInput): PaymentCalendarResult => {
  if (!tripStartDate) {
    return {
      kind: 'error',
      message: 'Payment calendar unavailable: trip start date is missing.',
    };
  }

  if (![flightCost, hotelCost, cabCost, totalPackageCost].every(isFiniteAmount)) {
    return {
      kind: 'error',
      message: 'Payment calendar unavailable: one or more quotation totals are invalid.',
    };
  }

  const createdDate = new Date(createdAt || getPdfRenderTimestamp());
  const tripDate = parseLocalDate(tripStartDate);

  if (Number.isNaN(createdDate.getTime())) {
    return {
      kind: 'error',
      message: 'Payment calendar unavailable: original quotation creation timestamp is invalid.',
    };
  }

  if (Number.isNaN(tripDate.getTime())) {
    return {
      kind: 'error',
      message: 'Payment calendar unavailable: trip start date is invalid.',
    };
  }

  const daysUntilTrip = Math.round(
    (startOfLocalDay(tripDate).getTime() - startOfLocalDay(createdDate).getTime()) / DAY_MS
  );
  const bookingDue = addHours(createdDate, 24);

  if (daysUntilTrip > 30) {
    const bookingAmount = Math.round(flightCost + 0.25 * (hotelCost + cabCost));
    const balanceAmount = Math.max(0, Math.round(totalPackageCost - bookingAmount));
    const finalDue = addCalendarDays(tripDate, -15);

    return {
      kind: 'ok',
      daysUntilTrip,
      milestones: [
        {
          step: 1,
          title: 'Booking Amount',
          amount: bookingAmount,
          dueAt: bookingDue.toISOString(),
          dueFormat: 'dateTime',
          note: 'Pay the booking amount to confirm your trip.',
        },
        {
          step: 2,
          title: 'Balance Payment',
          amount: balanceAmount,
          dueAt: toLocalDateKey(finalDue),
          dueFormat: 'date',
          note: 'Remaining package amount, payable 15 days before departure',
        },
      ],
    };
  }

  return {
    kind: 'ok',
    daysUntilTrip,
    milestones: [
      {
        step: 1,
        title: 'Full Payment',
        amount: Math.round(totalPackageCost),
        dueAt: bookingDue.toISOString(),
        dueFormat: 'dateTime',
        note: 'Full payment is required because the trip starts within 30 days.',
      },
    ],
  };
};
